(() => {
  'use strict';

  const store = window.GasNeedsCart;
  if (!store) return;

  const MAX_QUANTITY = store.maxQuantity || 10000;
  const timers = new WeakMap();

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function requestQuoteUrl() {
    const configured = document.querySelector('[data-gn-request-quote-url]')?.dataset.gnRequestQuoteUrl;
    return configured || `${window.routes?.rootUrl || '/'}pages/request-a-quote`;
  }

  function setStatus(root, message, error = false) {
    const status = root?.querySelector('[data-gn-status]') || document.querySelector('[data-gn-global-status]');
    if (!status) return;
    status.textContent = message;
    status.dataset.error = String(error);
  }

  function makeImage(item) {
    const link = element('a', 'gn-cart__image');
    link.href = store.productUrl(item);
    link.tabIndex = -1;
    link.setAttribute('aria-hidden', 'true');
    if (item.image) {
      const image = element('img');
      image.src = item.image;
      image.alt = '';
      image.loading = 'lazy';
      link.append(image);
    } else {
      link.append(element('span', 'gn-cart__placeholder', 'GASNEEDS'));
    }
    return link;
  }

  function makeRow(item, compact = false) {
    const row = element('li', compact ? 'gn-cart__item gn-cart__item--compact' : 'gn-cart__item');
    row.dataset.gnItemId = String(item.id);
    row.append(makeImage(item));

    const info = element('div', 'gn-cart__item-info');
    const title = element('a', 'gn-cart__title', item.title);
    title.href = store.productUrl(item);
    info.append(title);
    if (item.variant) info.append(element('p', 'gn-cart__detail', item.variant));
    if (item.sku && !compact) info.append(element('p', 'gn-cart__detail', `SKU: ${item.sku}`));
    if (item.requiresAvailability) info.append(element('p', 'gn-cart__availability', 'Availability to be confirmed'));

    const remove = element('button', 'gn-cart__remove', 'Remove');
    remove.type = 'button';
    remove.dataset.gnRemoveItem = String(item.id);
    remove.setAttribute('aria-label', `Remove ${item.title}`);
    info.append(remove);
    row.append(info);

    const quantity = element('div', 'gn-cart__quantity');
    const label = element('label', '', compact ? 'Qty' : 'Quantity');
    const input = element('input');
    input.type = 'number';
    input.inputMode = 'numeric';
    input.min = '1';
    input.max = String(MAX_QUANTITY);
    input.step = '1';
    input.required = true;
    input.value = String(item.quantity);
    input.dataset.gnQuantity = String(item.id);
    input.id = `GNQuantity-${item.id}-${Math.random().toString(36).slice(2, 7)}`;
    input.setAttribute('aria-label', `Quantity for ${item.title}`);
    label.htmlFor = input.id;
    quantity.append(label, input);
    row.append(quantity);
    return row;
  }

  function productSummary(items) {
    if (!items.length) return 'No catalogue products selected. See request details.';
    return items.map((item, index) => {
      const lines = [`${index + 1}. ${item.title}`];
      if (item.variant) lines.push(`Variant: ${item.variant}`);
      if (item.sku) lines.push(`SKU: ${item.sku}`);
      lines.push(`Quantity: ${item.quantity}`);
      if (item.requiresAvailability) lines.push('Availability: confirmation requested');
      lines.push(`Product: ${new URL(store.productUrl(item), window.location.origin).href}`);
      return lines.join('\n');
    }).join('\n\n');
  }

  function renderRoot(root) {
    const items = store.getItems();
    const list = root.querySelector('[data-gn-items]');
    if (list) list.replaceChildren(...items.map((item) => makeRow(item, root.hasAttribute('data-gn-mini'))));

    const empty = root.querySelector('[data-gn-empty]');
    const content = root.querySelector('[data-gn-has-items]');
    if (empty) empty.hidden = items.length > 0;
    if (content) content.hidden = items.length === 0;

    const error = store.getError();
    if (error) setStatus(root, error, true);

    const summary = root.querySelector('[data-gn-products]');
    if (summary) summary.value = productSummary(items);

    const notes = root.querySelector('[data-gn-notes]');
    const notesLabel = root.querySelector('[data-gn-notes-label]');
    if (notes) notes.required = items.length === 0;
    if (notesLabel) notesLabel.textContent = items.length ? '(optional)' : '(required without a product)';
  }

  function renderAll() {
    document.querySelectorAll('[data-gn-basket]').forEach(renderRoot);
  }

  function openMiniCart() {
    const mini = document.getElementById('mini-cart');
    const trigger = document.querySelector('[aria-controls="mini-cart"][data-action="toggle-mini-cart"]');
    if (mini && trigger && mini.getAttribute('aria-hidden') !== 'false') trigger.click();
    else if (!mini) window.location.assign(window.routes?.cartUrl || '/cart');
  }

  function quantityFromButton(button) {
    const input = button.closest('form')?.querySelector('[name="quantity"]');
    const quantity = input ? Number(input.value) : 1;
    if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > MAX_QUANTITY) {
      if (input) {
        input.setCustomValidity(`Enter a whole quantity from 1 to ${MAX_QUANTITY.toLocaleString()}.`);
        input.reportValidity();
        input.focus();
      }
      throw new Error(`Enter a whole quantity from 1 to ${MAX_QUANTITY.toLocaleString()}.`);
    }
    if (input) input.setCustomValidity('');
    return quantity;
  }

  function addFromButton(button) {
    const quantity = quantityFromButton(button);
    const requiresAvailability = button.dataset.gnAvailability === 'true';
    store.add({
      id: Number(button.dataset.gnId),
      handle: button.dataset.gnHandle || '',
      title: button.dataset.gnTitle || '',
      variant: button.dataset.gnVariant || '',
      sku: button.dataset.gnSku || '',
      image: button.dataset.gnImage || '',
      quantity,
      requiresAvailability
    });

    const original = button.dataset.gnOriginalLabel || button.textContent.trim();
    button.dataset.gnOriginalLabel = original;
    button.textContent = requiresAvailability ? 'Added — Availability Pending' : 'Added to Cart';
    button.classList.add('is-added');
    clearTimeout(timers.get(button));
    timers.set(button, setTimeout(() => {
      if (!button.isConnected) return;
      button.textContent = original;
      button.classList.remove('is-added');
    }, 1800));
    openMiniCart();
  }

  let pendingClearRoot = null;
  function clearDialog() {
    let dialog = document.querySelector('[data-gn-clear-dialog]');
    if (dialog) return dialog;
    dialog = element('div', 'gn-clear-dialog');
    dialog.dataset.gnClearDialog = '';
    dialog.hidden = true;
    dialog.innerHTML = '<div class="gn-clear-dialog__backdrop" data-gn-cancel-clear></div><div class="gn-clear-dialog__panel" role="dialog" aria-modal="true" aria-labelledby="GNClearTitle"><h2 id="GNClearTitle">Clear your cart?</h2><p>This removes every saved product from this browser.</p><div class="gn-clear-dialog__actions"><button type="button" class="button button--ternary" data-gn-cancel-clear>Cancel</button><button type="button" class="button button--primary" data-gn-confirm-clear>Clear all</button></div></div>';
    document.body.append(dialog);
    return dialog;
  }

  function showClearDialog(root) {
    pendingClearRoot = root;
    const dialog = clearDialog();
    dialog.hidden = false;
    document.documentElement.classList.add('gn-clear-dialog-open');
    dialog.querySelector('button[data-gn-cancel-clear]').focus();
  }

  function hideClearDialog() {
    const dialog = document.querySelector('[data-gn-clear-dialog]');
    if (dialog) dialog.hidden = true;
    document.documentElement.classList.remove('gn-clear-dialog-open');
    pendingClearRoot = null;
  }

  function validQuantity(input) {
    const quantity = Number(input.value);
    input.setCustomValidity(Number.isSafeInteger(quantity) && quantity >= 1 && quantity <= MAX_QUANTITY ? '' : `Enter a whole quantity from 1 to ${MAX_QUANTITY.toLocaleString()}.`);
    return input.checkValidity();
  }

  function validateQuoteForm(form) {
    const name = form.querySelector('[data-gn-name]');
    const phone = form.querySelector('[data-gn-phone]');
    const email = form.querySelector('[name="contact[email]"]');
    const notes = form.querySelector('[data-gn-notes]');
    if (name) {
      const value = name.value.trim();
      name.setCustomValidity(/\p{L}/u.test(value) && /^[\p{L}\p{M} '’\-]+$/u.test(value) ? '' : 'Full name is required. Use letters, spaces, hyphens or apostrophes only.');
    }
    if (phone) {
      const value = phone.value.trim();
      const digits = value.replace(/\D/g, '');
      phone.setCustomValidity(value && /^\+?[\d\s().-]+$/.test(value) && digits.length >= 7 && digits.length <= 15 ? '' : 'Use 7–15 digits, with spaces, hyphens, parentheses or a leading +.');
    }
    if (email) email.setCustomValidity(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.value.trim()) ? '' : 'Enter a complete email address, for example name@domain.com.');
    if (notes) notes.setCustomValidity(notes.required && !notes.value.trim() ? 'Describe the products or services you need.' : '');
  }

  document.addEventListener('click', (event) => {
    const add = event.target.closest('[data-gn-add-item]');
    if (add) {
      event.preventDefault();
      if (add.disabled) return;
      try { addFromButton(add); }
      catch (error) {
        const root = add.closest('[data-gn-basket]');
        setStatus(root, error.message, true);
      }
      return;
    }

    const remove = event.target.closest('[data-gn-remove-item]');
    if (remove) {
      event.preventDefault();
      const root = remove.closest('[data-gn-basket]');
      try {
        store.remove(Number(remove.dataset.gnRemoveItem));
        setStatus(root, 'Product removed.');
      } catch (error) { setStatus(root, error.message, true); }
      return;
    }

    const clear = event.target.closest('[data-gn-clear]');
    if (clear) {
      event.preventDefault();
      showClearDialog(clear.closest('[data-gn-basket]'));
      return;
    }

    if (event.target.closest('[data-gn-confirm-clear]')) {
      try {
        store.clear();
        setStatus(pendingClearRoot, 'Cart cleared.');
      } catch (error) { setStatus(pendingClearRoot, error.message, true); }
      hideClearDialog();
      return;
    }

    if (event.target.closest('[data-gn-cancel-clear]')) hideClearDialog();
  });

  document.addEventListener('input', (event) => {
    const input = event.target.closest('[data-gn-quantity]');
    if (input) validQuantity(input);
    const form = event.target.closest('[data-gn-contact]');
    if (form) validateQuoteForm(form);
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-gn-quantity]');
    if (!input || !validQuantity(input)) {
      if (input) input.reportValidity();
      return;
    }
    const root = input.closest('[data-gn-basket]');
    try {
      store.setQuantity(Number(input.dataset.gnQuantity), Number(input.value));
      setStatus(root, 'Quantity saved.');
    } catch (error) { setStatus(root, error.message, true); }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-gn-contact]');
    if (!form) return;
    validateQuoteForm(form);
    const summary = form.querySelector('[data-gn-products]');
    if (summary) summary.value = productSummary(store.getItems());
    if (!form.checkValidity()) {
      event.preventDefault();
      form.reportValidity();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && document.querySelector('[data-gn-clear-dialog]:not([hidden])')) hideClearDialog();
  });

  async function importLegacyLink() {
    const url = new URL(window.location.href);
    const handle = url.searchParams.get('gn_product');
    if (!handle || !/^[a-z0-9][a-z0-9-]{0,254}$/.test(handle)) return;
    try {
      const response = await fetch(`${window.routes?.rootUrl || '/'}products/${encodeURIComponent(handle)}.js`, {credentials: 'same-origin'});
      if (!response.ok) return;
      const product = await response.json();
      const requestedId = Number(url.searchParams.get('variant'));
      const variant = product.variants.find((entry) => entry.id === requestedId) || product.variants.find((entry) => entry.available) || product.variants[0];
      if (!variant) return;
      store.add({
        id: variant.id,
        handle: product.handle,
        title: product.title,
        variant: variant.title,
        sku: variant.sku,
        image: product.featured_image || product.images?.[0] || '',
        quantity: 1,
        requiresAvailability: !variant.available
      });
      url.searchParams.delete('gn_product');
      history.replaceState({}, '', url);
    } catch (_) {
      // Old links are optional compatibility; normal catalogue actions remain available.
    }
  }

  function boot() {
    const success = document.querySelector('[data-gn-success]');
    if (success && !sessionStorage.getItem('gn:quote-success-cleared')) {
      try { store.clear(); } catch (_) { /* The success message still remains valid. */ }
      sessionStorage.setItem('gn:quote-success-cleared', 'true');
      success.focus();
    } else if (!success) {
      sessionStorage.removeItem('gn:quote-success-cleared');
    }
    importLegacyLink().finally(renderAll);
  }

  document.addEventListener('gn:catalog-cart-changed', renderAll);
  document.addEventListener('shopify:section:load', renderAll);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, {once: true});
  else boot();
})();
